/**
 * 부 단계의 우 detail — 그 부의 모든 류 popum row 표시 (image 218).
 *
 * Props:
 *   section: 1-21 (부 id)
 *
 * 화면 (image 218 패턴):
 *   | 류 | 품명 한글 | 품명 영문 |
 *   | 94 (yellow) | 가구, 침구·매트리스... | Furniture; bedding... |
 *   | 95 (yellow) | 완구·게임용구·운동용구... | Toys, games and sports requisites... |
 *   ...
 *
 * 행 클릭 → URL ?chapter=NN 으로 navigate (류 단계 진입).
 *
 * Fetch: /api/onto/section-chapters?section=N
 */
import React, { useState, useEffect } from "react";
import { CHAPTERS_97 } from "./chaptersData";

function chapterShortName(ch) {
  const found = CHAPTERS_97.find((c) => c.ch === ch);
  return found ? found.name : "";
}

function navigateToChapter(chapter) {
  const u = new URL(window.location.href);
  u.searchParams.set("audit", "onto-detail");
  u.searchParams.set("chapter", chapter);
  u.searchParams.delete("section");
  u.searchParams.delete("hs");
  u.searchParams.delete("hsk");
  window.history.pushState({}, "", u.toString());
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export default function SectionChaptersGrid({ section }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!section || !/^[0-9]{1,2}$/.test(String(section))) {
      setData({ chapters: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    fetch(`/api/onto/section-chapters?section=${section}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d || { chapters: [] }))
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }, [section]);

  const chapters = data?.chapters || [];

  if (loading) {
    return (
      <div
        style={{ color: "var(--audit-text-muted)", fontSize: 13, padding: 8 }}
      >
        류 목록 불러오는 중…
      </div>
    );
  }
  if (err) {
    return (
      <div
        style={{
          color: "#d93025",
          fontSize: 12,
          padding: 10,
          border: "1px solid #d93025",
          borderRadius: 4,
        }}
      >
        오류: {err}
      </div>
    );
  }
  if (chapters.length === 0) {
    return (
      <div
        style={{
          padding: 16,
          color: "var(--audit-text-muted)",
          fontSize: 13,
          fontStyle: "italic",
          border: "1px dashed var(--audit-border)",
          borderRadius: 6,
          background: "var(--audit-bg-subtle)",
        }}
      >
        제 {section} 부에 등록된 류 없음.
      </div>
    );
  }

  return (
    <table
      style={{
        width: "100%",
        fontSize: 12,
        borderCollapse: "collapse",
        background: "var(--audit-bg-elevated)",
        border: "1px solid var(--audit-border)",
        tableLayout: "fixed",
      }}
    >
      <colgroup>
        <col style={{ width: 60 }} />
        <col />
        <col />
      </colgroup>
      <thead>
        <tr style={{ background: "var(--audit-bg-subtle)" }}>
          <th rowSpan={2} style={thStyle}>
            류
          </th>
          <th colSpan={2} style={thStyle}>
            품명 (한국 2026년)
          </th>
        </tr>
        <tr style={{ background: "var(--audit-bg-subtle)" }}>
          <th
            style={{ ...thStyle, borderTop: "1px solid var(--audit-border)" }}
          >
            한글
          </th>
          <th
            style={{ ...thStyle, borderTop: "1px solid var(--audit-border)" }}
          >
            영문
          </th>
        </tr>
      </thead>
      <tbody>
        {chapters.map(({ chapter, popum_ko, popum_en }) => (
          <tr
            key={chapter}
            onClick={() => navigateToChapter(chapter)}
            style={{
              borderTop: "1px solid var(--audit-border)",
              cursor: "pointer",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--audit-bg-hover)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = "")}
          >
            <td style={chCellStyle}>{parseInt(chapter, 10)}</td>
            <td style={koCellStyle}>
              {popum_ko || chapterShortName(chapter) || ""}
            </td>
            <td style={enCellStyle}>{popum_en || ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const thStyle = {
  textAlign: "center",
  padding: "6px 8px",
  borderBottom: "1px solid var(--audit-border-strong)",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--audit-text-muted)",
};

const chCellStyle = {
  padding: "10px 4px",
  textAlign: "center",
  verticalAlign: "top",
  background: "transparent",
  color: "var(--audit-text)",
  fontFamily: "JetBrains Mono, monospace",
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.5px",
  borderRight: "1px solid var(--audit-border)",
};

const koCellStyle = {
  padding: "10px 12px",
  verticalAlign: "top",
  fontSize: 12,
  color: "var(--audit-text)",
  lineHeight: 1.65,
  whiteSpace: "normal",
  wordBreak: "keep-all",
  overflowWrap: "anywhere", // 한·영 혼합 긴 토큰이 좁은 셀(모바일)을 넘지 않도록
  borderRight: "1px solid var(--audit-border)",
};

const enCellStyle = {
  padding: "10px 12px",
  verticalAlign: "top",
  fontSize: 12,
  color: "var(--audit-text-muted)",
  lineHeight: 1.55,
  whiteSpace: "normal",
};
